import React from 'react';
import PropTypes from 'prop-types';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const LiveAnnouncerContext = React.createContext(null);

export const LiveAnnouncer = ({ children }) => {
    const [message, setMessage] = React.useState('');

    const announce = React.useCallback((msg) => {
        setMessage('');
        requestAnimationFrame(() => setMessage(msg));
    }, []);

    const value = React.useMemo(() => ({ announce }), [announce]);

    return (
        <LiveAnnouncerContext.Provider value={value}>
            {children}
            <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
                {message}
            </VisuallyHidden>
        </LiveAnnouncerContext.Provider>
    );
};

LiveAnnouncer.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAnnounce = () => {
    const context = React.useContext(LiveAnnouncerContext);
    if (!context) {
        throw new Error('useAnnounce must be used within a LiveAnnouncer');
    }
    return context.announce;
};

export const useDebouncedAnnounce = (message, delay = 0) => {
    const announce = useAnnounce();
    const timerRef = React.useRef(null);

    React.useEffect(() => {
        clearTimeout(timerRef.current);

        if (!message) {
            return undefined;
        }

        timerRef.current = setTimeout(() => {
            announce(message);
        }, delay);

        return () => clearTimeout(timerRef.current);
    }, [message, delay, announce]);
};
